import { Component, EventBus } from '../core/smpl-components.js'
import { Events } from '../core/events.js'
import { gsap } from 'gsap'

// -----------------------------------------------------------------------------
// Cart — paints the cart counter after an AJAX add.
//
// Listens for CART_UPDATED and applies the WooCommerce fragments it carries: a
// map of { selector: html } that Woo builds through the
// `woocommerce_add_to_cart_fragments` filter (see functions/woocommerce.php).
// Applying them generically means a mini-cart fragment added later needs no JS
// change — only a PHP filter and the matching markup inside this element.
//
// Only nodes *inside* this element are replaced, never the element itself, so
// the mounted instance survives. Selector: `.js-cart`.
//
// After an add it also pins itself to the top of the viewport with a notice
// ("toegevoegd aan je winkelwagen"), so the cart is in view wherever the visitor
// has scrolled to, and fades away again after NOTICE_DURATION. Another add while
// it is pinned just restarts the timer. `is-pinned` only switches the layout
// (_objects/_shopping-cart.scss); the motion is GSAP: a short fade-and-slide,
// in and out the way the menu items move.
// -----------------------------------------------------------------------------
const NOTICE_DURATION = 5000

export default class extends Component {
	prepare() {
		this.bndOnCartUpdated = this.onCartUpdated.bind(this)
		this.bndHideNotice = this.hideNotice.bind(this)
		EventBus.on(Events.CART_UPDATED, this.bndOnCartUpdated)
	}

	mount() {}

	unmount() {
		EventBus.off(Events.CART_UPDATED, this.bndOnCartUpdated)
		clearTimeout(this.noticeTimeout)
		gsap.killTweensOf(this.element)
	}

	onCartUpdated({ fragments }) {
		if (!fragments) return

		Object.entries(fragments).forEach(([selector, html]) => this.applyFragment(selector, html))
		this.showNotice()
	}

	showNotice() {
		clearTimeout(this.noticeTimeout)
		this.noticeTimeout = setTimeout(this.bndHideNotice, NOTICE_DURATION)

		// Already showing: leave it where it is. A second add while it is still
		// leaving picks it back up from wherever the exit got to.
		if (this.isNoticeVisible) return
		this.isNoticeVisible = true

		if (!this.element.classList.contains('is-pinned')) {
			this.element.classList.add('is-pinned')
			gsap.set(this.element, { autoAlpha: 0, yPercent: -30 })
		}

		gsap.to(this.element, { autoAlpha: 1, yPercent: 0, duration: 0.5, ease: 'elastic.out(2,1.72)', overwrite: true })
	}

	hideNotice() {
		this.isNoticeVisible = false

		gsap.to(this.element, {
			autoAlpha: 0,
			yPercent: -30,
			duration: 0.25,
			ease: 'power2.in',
			overwrite: true,
			// Unpinned once it has faded out, so it drops back to its spot at
			// the top of the page.
			onComplete: () => {
				this.element.classList.remove('is-pinned')
				gsap.set(this.element, { clearProps: 'transform,opacity,visibility' })
			},
		})
	}

	applyFragment(selector, html) {
		const target = this.element.querySelector(selector)
		if (!target || typeof html !== 'string') return

		// outerHTML rather than innerHTML: the fragment markup includes the
		// element itself, which is how WooCommerce fragments are shaped.
		target.outerHTML = html

		return this.element.querySelector(selector)
	}
}
